import React, { useState, useEffect, useCallback, useRef } from "react";
import { exportSubscriptionsCsv, fetchSubscriptionsAPI, firstError, updateSubscriptionAPI } from "../../utils/api";
import { showConfirmation, showError, showSuccess } from "../../utils/sweetAlert";
import { EmptyState, ErrorAlert, LoadingState, Modal, Pagination, SearchField, StatCard } from "../../components/ui";
import GrantSubscriptionModal from "../../components/GrantSubscriptionModal";
import "./Subscriptions.css";

// Statuses as the server works them out (effective_status): a paid plan past its end date is "expired"
const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "expired", label: "Expired" },
  { key: "pending", label: "Pending", title: "Checkout opened but never paid" },
  { key: "failed", label: "Failed", title: "Payment could not be verified" },
  { key: "cancelled", label: "Cancelled" },
];

const STATUS_LABELS = { pending: "Payment pending" };

const DAY = 24 * 60 * 60 * 1000;

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric", timeZone: "Asia/Kolkata" });
};

const formatINR = (value) => `₹${(Number(value) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const statusOf = (sub) => sub.effective_status || sub.status || "active";
const statusLabel = (sub) => {
  const status = statusOf(sub);
  return STATUS_LABELS[status] || status.charAt(0).toUpperCase() + status.slice(1);
};

const statusTone = (status) =>
  ({
    active: "badge-success",
    expired: "badge-danger",
    cancelled: "badge-warning",
    failed: "badge-danger",
    pending: "badge-neutral",
  })[status] || "badge-neutral";

const isPaid = (sub) => Boolean(sub.razorpay_payment_id && String(sub.razorpay_payment_id).trim());
const isManual = (sub) => sub.source === "manual";

const daysLeft = (sub) => {
  if (!sub.expires_at) return null;
  // expires_at is a date: access lasts to the end of that day
  const diff = new Date(sub.expires_at).getTime() + DAY - Date.now();
  return isNaN(diff) ? null : Math.max(0, Math.floor(diff / DAY));
};

function Subscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [totals, setTotals] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [perPage, setPerPage] = useState(25);
  const [selected, setSelected] = useState(null);
  const [grantOpen, setGrantOpen] = useState(false);
  const [extendOpen, setExtendOpen] = useState(false);
  const [extend, setExtend] = useState({ months: "1", days: "" });
  const [acting, setActing] = useState(false);
  const [exporting, setExporting] = useState(false);

  const firstLoad = useRef(true);
  const currentPage = pagination?.current_page || 1;
  const totalPages = pagination?.last_page || 1;

  const filters = useCallback(
    () => ({ search: searchTerm.trim(), status: statusFilter, from, to }),
    [searchTerm, statusFilter, from, to]
  );

  const fetchSubscriptions = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        setError(null);
        const result = await fetchSubscriptionsAPI({ ...filters(), page, per_page: perPage });
        if (result?.success) {
          setSubscriptions(result.data?.data || []);
          setPagination(result.data || null);
          setTotals(result.totals || null);
        } else if (result) {
          setError(firstError(result, "Failed to load subscriptions."));
        }
      } catch (err) {
        console.error("Error fetching subscriptions:", err);
        setError("Couldn't reach the server to load subscriptions.");
      } finally {
        setLoading(false);
      }
    },
    [filters, perPage]
  );

  // Filters are applied by the server; typing in the search waits a moment before reloading
  useEffect(() => {
    const delay = firstLoad.current ? 0 : 450;
    firstLoad.current = false;
    const timer = setTimeout(() => fetchSubscriptions(1), delay);
    return () => clearTimeout(timer);
  }, [fetchSubscriptions]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      fetchSubscriptions(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await exportSubscriptionsCsv(filters());
    } catch (err) {
      showError(err.message || "Couldn't export subscriptions.");
    } finally {
      setExporting(false);
    }
  };

  // After an admin action: update the open dialog and the row, then refresh the totals
  const applyChange = (updated) => {
    setSelected(updated);
    setSubscriptions((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    fetchSubscriptions(currentPage);
  };

  const runAction = async (payload, successTitle) => {
    setActing(true);
    try {
      const response = await updateSubscriptionAPI(selected.id, payload);
      if (!response) return false;
      if (!response.success) {
        showError(firstError(response, "Couldn't update the subscription."));
        return false;
      }
      showSuccess(response.message, successTitle);
      applyChange(response.data);
      return true;
    } finally {
      setActing(false);
    }
  };

  const handleCancel = async () => {
    const result = await showConfirmation(
      `${selected.user?.name || "The student"} loses access now. The payment record is kept; you can reactivate it later.`,
      "Cancel this subscription?",
      "Cancel subscription",
      "Keep it",
      "danger"
    );
    if (result.isConfirmed) runAction({ action: "cancel" }, "Subscription cancelled");
  };

  const handleReactivate = () => runAction({ action: "reactivate" }, "Subscription reactivated");

  const handleExtend = async () => {
    const months = parseInt(extend.months, 10) || 0;
    const days = parseInt(extend.days, 10) || 0;
    if (months < 0 || days < 0 || months + days === 0) {
      showError("Enter how many months or days to add.");
      return;
    }
    const ok = await runAction(
      { action: "extend", ...(months ? { months } : {}), ...(days ? { days } : {}) },
      "Subscription extended"
    );
    if (ok) setExtendOpen(false);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setFrom("");
    setTo("");
  };

  const hasFilters = searchTerm.trim() || statusFilter !== "all" || from || to;

  const renderStudent = (sub) => {
    const user = sub.user || {};
    return (
      <div className="cell-user">
        <span className="avatar">{(user.name || "?").charAt(0)}</span>
        <div className="cell-stack">
          <strong>{user.name || "Unknown user"}</strong>
          <small>{user.email || "—"}</small>
        </div>
      </div>
    );
  };

  const renderExpiry = (sub) => {
    const d = daysLeft(sub);
    const active = statusOf(sub) === "active";
    const soon = active && d !== null && d <= 7;
    return (
      <div className="cell-stack">
        <strong>{formatDate(sub.expires_at)}</strong>
        <small className={soon ? "subs-soon" : ""}>
          {active && d !== null ? `${d} day${d === 1 ? "" : "s"} left` : `Started ${formatDate(sub.starts_at)}`}
        </small>
      </div>
    );
  };

  const renderPayment = (sub) => {
    if (isPaid(sub)) {
      return (
        <span className="mono subs-payment-id" title={sub.razorpay_payment_id}>
          {sub.razorpay_payment_id}
        </span>
      );
    }
    if (isManual(sub)) {
      return (
        <span className="badge badge-info" title={sub.note || undefined}>
          <i className="bi bi-gift"></i> Granted
        </span>
      );
    }
    return <span className="badge badge-neutral">Unpaid</span>;
  };

  const detail = (label, value, span) =>
    value || value === 0 ? (
      <div className={`detail-cell ${span ? "span-2" : ""}`}>
        <span className="detail-label">{label}</span>
        <span className="detail-value">{value}</span>
      </div>
    ) : null;

  const selectedStatus = selected ? statusOf(selected) : null;
  const canExtend = selected && ["active", "expired"].includes(selectedStatus);

  return (
    <div className="subscriptions-page page">
      <div className="page-header">
        <div>
          <h2>Subscriptions</h2>
          <p>Every purchase, renewal and its payment details.</p>
        </div>
        <div className="header-actions">
          <button type="button" className="btn-secondary" onClick={() => fetchSubscriptions(currentPage)} disabled={loading}>
            <i className={`bi bi-arrow-clockwise ${loading ? "spin" : ""}`}></i> Refresh
          </button>
          <button type="button" className="btn-secondary" onClick={handleExport} disabled={exporting}>
            {exporting ? <span className="btn-spinner"></span> : <i className="bi bi-download"></i>} Export CSV
          </button>
          <button type="button" className="btn-primary" onClick={() => setGrantOpen(true)}>
            <i className="bi bi-gift"></i> Grant plan
          </button>
        </div>
      </div>

      {grantOpen && <GrantSubscriptionModal onClose={() => setGrantOpen(false)} onGranted={() => fetchSubscriptions(1)} />}

      <ErrorAlert message={error} onRetry={() => fetchSubscriptions(currentPage)} />

      <div className="stats-grid">
        <StatCard
          icon="bi bi-collection"
          tone="brand"
          value={Number(totals?.all ?? pagination?.total ?? 0).toLocaleString("en-IN")}
          label="Subscriptions"
          hint={hasFilters && statusFilter === "all" ? "Matching the filters" : undefined}
        />
        <StatCard icon="bi bi-patch-check" tone="success" value={Number(totals?.active ?? 0)} label="Active" />
        <StatCard
          icon="bi bi-currency-rupee"
          tone="info"
          value={formatINR(totals?.revenue)}
          label="Revenue"
          hint={from || to ? "Paid online, in this date range" : "Paid online"}
        />
        <StatCard icon="bi bi-hourglass-split" tone="warning" value={Number(totals?.expiring_7d ?? 0)} label="Expiring in 7 days" />
      </div>

      <div className="toolbar">
        <SearchField value={searchTerm} onChange={setSearchTerm} placeholder="Search name, email, phone or Razorpay ID…" />
        <div className="chip-group">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              title={f.title}
              className={`chip ${statusFilter === f.key ? "active" : ""}`}
              onClick={() => setStatusFilter(f.key)}
            >
              {f.label}
              {totals && <span className="chip-count">{Number(totals[f.key] ?? 0)}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="toolbar subs-toolbar-dates">
        <label className="subs-date">
          <span>Purchased from</span>
          <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="subs-date">
          <span>to</span>
          <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
        </label>
        {hasFilters && (
          <button type="button" className="btn-ghost btn-sm" onClick={clearFilters}>
            Clear filters
          </button>
        )}
        <label className="subs-date subs-per-page">
          <span>Rows</span>
          <select className="select-sm" value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}>
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="table-card">
        {loading ? (
          <LoadingState message="Loading subscriptions…" />
        ) : subscriptions.length === 0 ? (
          !error && (
            <div className="subs-empty">
              <EmptyState
                icon={hasFilters ? "bi bi-funnel" : "bi bi-inbox"}
                title={hasFilters ? "No matching subscriptions" : "No subscriptions yet"}
                message={
                  hasFilters
                    ? "Try a different search, status or date range."
                    : "Subscriptions appear here once students purchase a plan."
                }
                action={
                  hasFilters && (
                    <button type="button" className="btn-secondary" onClick={clearFilters}>
                      Clear filters
                    </button>
                  )
                }
              />
            </div>
          )
        ) : (
          <>
            <div className="table-scroll subs-table-view">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Plan</th>
                    <th>Amount</th>
                    <th>Expires</th>
                    <th>Status</th>
                    <th>Payment</th>
                    <th aria-label="Actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((sub) => (
                    <tr key={sub.id}>
                      <td>{renderStudent(sub)}</td>
                      <td>
                        <div className="cell-stack">
                          <strong>{sub.course_plan?.plan_name || sub.user?.course || "—"}</strong>
                          <small>
                            {sub.course_plan?.course?.course_name || ""}
                            {sub.is_renewal ? " · Renewal" : ""}
                          </small>
                        </div>
                      </td>
                      <td>
                        <div className="cell-stack">
                          <strong>{formatINR(sub.amount)}</strong>
                          {sub.course_plan?.plan_amount && Number(sub.course_plan.plan_amount) !== Number(sub.amount) && (
                            <small>List {formatINR(sub.course_plan.plan_amount)}</small>
                          )}
                        </div>
                      </td>
                      <td>{renderExpiry(sub)}</td>
                      <td>
                        <span className={`badge badge-dot ${statusTone(statusOf(sub))}`}>{statusLabel(sub)}</span>
                      </td>
                      <td>{renderPayment(sub)}</td>
                      <td>
                        <button type="button" className="btn-secondary btn-sm" onClick={() => setSelected(sub)}>
                          Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="subs-card-list">
              {subscriptions.map((sub) => (
                <div key={sub.id} className="subs-card">
                  <div className="subs-card-head">
                    {renderStudent(sub)}
                    <span className={`badge badge-dot ${statusTone(statusOf(sub))}`}>{statusLabel(sub)}</span>
                  </div>
                  <div className="subs-card-grid">
                    <div>
                      <span>Plan</span>
                      <strong>{sub.course_plan?.plan_name || sub.user?.course || "—"}</strong>
                    </div>
                    <div>
                      <span>Amount</span>
                      <strong>{formatINR(sub.amount)}</strong>
                    </div>
                    <div>
                      <span>Starts</span>
                      <strong>{formatDate(sub.starts_at)}</strong>
                    </div>
                    <div>
                      <span>Expires</span>
                      <strong>{formatDate(sub.expires_at)}</strong>
                    </div>
                  </div>
                  <button type="button" className="btn-secondary btn-sm btn-block" onClick={() => setSelected(sub)}>
                    View details
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {!loading && pagination && pagination.total > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            info={`Showing ${pagination.from ?? 0}–${pagination.to ?? 0} of ${Number(pagination.total).toLocaleString("en-IN")}`}
          />
        )}
      </div>

      <Modal
        open={Boolean(selected) && !extendOpen}
        onClose={() => !acting && setSelected(null)}
        size="lg"
        icon="bi bi-receipt"
        title="Subscription details"
        subtitle={selected ? `#${selected.id} · ${selected.user?.name || "Unknown user"}` : undefined}
        footer={
          selected && (
            <div className="subs-actions">
              {canExtend && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setExtend({ months: "1", days: "" });
                    setExtendOpen(true);
                  }}
                  disabled={acting}
                >
                  <i className="bi bi-calendar-plus"></i> Extend
                </button>
              )}
              {selectedStatus === "active" && (
                <button type="button" className="btn-danger" onClick={handleCancel} disabled={acting}>
                  <i className="bi bi-x-circle"></i> Cancel subscription
                </button>
              )}
              {selectedStatus === "cancelled" && (
                <button type="button" className="btn-secondary" onClick={handleReactivate} disabled={acting}>
                  <i className="bi bi-arrow-counterclockwise"></i> Reactivate
                </button>
              )}
              <button type="button" className="btn-secondary subs-actions-close" onClick={() => setSelected(null)}>
                Close
              </button>
            </div>
          )
        }
      >
        {selected && (
          <>
            <div className="subs-summary">
              <div>
                <span>{isManual(selected) ? "Amount recorded" : "Amount paid"}</span>
                <strong>{formatINR(selected.amount)}</strong>
              </div>
              <span className={`badge badge-dot ${statusTone(selectedStatus)}`}>{statusLabel(selected)}</span>
            </div>

            <div className="detail-section">
              <div className="detail-section-title">
                <i className="bi bi-person"></i> Student
              </div>
              <div className="detail-grid">
                {detail("Name", selected.user?.name)}
                {detail("Phone", selected.user?.contact_number)}
                {detail("Email", selected.user?.email, true)}
                {detail("Course", selected.user?.course)}
                {detail("Location", [selected.user?.city, selected.user?.state].filter(Boolean).join(", ") || null)}
              </div>
            </div>

            <div className="detail-section">
              <div className="detail-section-title">
                <i className="bi bi-calendar-range"></i> Subscription
              </div>
              <div className="detail-grid">
                {detail("Starts", formatDate(selected.starts_at))}
                {detail("Expires", formatDate(selected.expires_at))}
                {detail("Validity", selected.validity_days ? `${selected.validity_days} days` : null)}
                {detail("Renewal", selected.is_renewal ? "Yes" : "No")}
                {detail("Source", isManual(selected) ? "Granted by an admin" : "Online payment")}
                {detail("Created", formatDate(selected.created_at))}
                {detail("Note", selected.note ? <span className="subs-note">{selected.note}</span> : null, true)}
              </div>
            </div>

            <div className="detail-section">
              <div className="detail-section-title">
                <i className="bi bi-card-list"></i> Plan
              </div>
              <div className="detail-grid">
                {detail("Plan", selected.course_plan?.plan_name || "—")}
                {detail("Course", selected.course_plan?.course?.course_name)}
                {detail("Duration", selected.course_plan?.plan_duration ? `${selected.course_plan.plan_duration} months` : null)}
                {detail("List price", selected.course_plan?.plan_amount ? formatINR(selected.course_plan.plan_amount) : null)}
                {detail(
                  "Discount",
                  Number(selected.course_plan?.plan_discount) > 0 ? formatINR(selected.course_plan.plan_discount) : null
                )}
                {detail("Description", selected.course_plan?.plan_description, true)}
              </div>
            </div>

            {!isManual(selected) && (
              <div className="detail-section">
                <div className="detail-section-title">
                  <i className="bi bi-shield-lock"></i> Payment (Razorpay)
                </div>
                <div className="detail-grid">
                  {detail("Payment ID", <span className="mono">{selected.razorpay_payment_id || "—"}</span>, true)}
                  {detail("Order ID", <span className="mono">{selected.razorpay_order_id || "—"}</span>, true)}
                </div>
              </div>
            )}
          </>
        )}
      </Modal>

      <Modal
        open={extendOpen}
        onClose={() => !acting && setExtendOpen(false)}
        icon="bi bi-calendar-plus"
        title="Extend subscription"
        subtitle={
          selected
            ? `${selected.user?.name || "Student"} · ${
                selectedStatus === "expired" ? "expired, so the time is added from today" : `ends ${formatDate(selected.expires_at)}`
              }`
            : undefined
        }
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setExtendOpen(false)} disabled={acting}>
              Back
            </button>
            <button type="button" className="btn-primary" onClick={handleExtend} disabled={acting}>
              {acting && <span className="btn-spinner"></span>} Extend
            </button>
          </>
        }
      >
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="extend-months">Months</label>
            <input
              id="extend-months"
              type="number"
              min="0"
              step="1"
              value={extend.months}
              onChange={(e) => setExtend((prev) => ({ ...prev, months: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label htmlFor="extend-days">Days</label>
            <input
              id="extend-days"
              type="number"
              min="0"
              step="1"
              value={extend.days}
              onChange={(e) => setExtend((prev) => ({ ...prev, days: e.target.value }))}
              placeholder="0"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Subscriptions;
